/**
 * Navigation tool definitions for the hands-free voice co-pilot.
 *
 * Each tool maps to an existing on-screen action. Enum-constrained parameters
 * (candidate ids) make the model's choice deterministic — the spoken name is
 * mapped to an exact id by the model, and the client dispatches on that id with
 * no fuzzy matching. tool_choice stays 'auto' at the session level.
 */

import type { ToolDefinition } from '@selna/session';

export const NAV_TOOL = {
  CONFIRM_START: 'confirm_start',
  READ_AGAIN: 'read_again',
  SELECT_CHARACTER: 'select_character',
  NEXT_EPISODE: 'next_episode',
  RESOLVE_CASE: 'resolve_case',
} as const;

/** Re-read the current screen text. */
export const READ_AGAIN_TOOL: ToolDefinition = {
  type: 'function',
  name: NAV_TOOL.READ_AGAIN,
  description: 'Lê novamente o texto do ecrã. Chama quando o utilizador pedir para repetir, voltar a ler, ou disser que não percebeu.',
  parameters: { type: 'object', properties: {}, required: [] },
};

// ── Episode-ready screen ─────────────────────────────────────────────────────

/** Start the conversation. */
export const CONFIRM_START_TOOL: ToolDefinition = {
  type: 'function',
  name: NAV_TOOL.CONFIRM_START,
  description: 'Inicia a conversa do episódio. Chama quando o utilizador disser algo como "começar", "vamos lá", "iniciar", "ok", "avança" ou "sim".',
  parameters: { type: 'object', properties: {}, required: [] },
};

export const EPISODE_READY_TOOLS: ToolDefinition[] = [CONFIRM_START_TOOL, READ_AGAIN_TOOL];

// ── Waiting / choose-next screen ─────────────────────────────────────────────

/** Continue to the already-prepared next episode. */
export const NEXT_EPISODE_TOOL: ToolDefinition = {
  type: 'function',
  name: NAV_TOOL.NEXT_EPISODE,
  description: 'Avança para o próximo episódio que já está pronto. Chama quando o utilizador disser "próximo episódio", "continuar" ou "avançar".',
  parameters: { type: 'object', properties: {}, required: [] },
};

/** Start the final resolve conversation with the authority. */
export const RESOLVE_CASE_TOOL: ToolDefinition = {
  type: 'function',
  name: NAV_TOOL.RESOLVE_CASE,
  description: 'Inicia a conversa de resolução do caso com a autoridade. Chama quando o utilizador disser "resolver o caso", "iniciar resolução" ou "já sei quem foi".',
  parameters: { type: 'object', properties: {}, required: [] },
};

/**
 * Build the select_character tool with the current candidates as an enum.
 * The model receives a spoken name and returns the exact candidate id.
 */
export function buildSelectCharacterTool(candidateIds: string[]): ToolDefinition {
  return {
    type: 'function',
    name: NAV_TOOL.SELECT_CHARACTER,
    description:
      'Escolhe a próxima pessoa a entrevistar. O utilizador diz o NOME da pessoa (pode dizer só o primeiro nome, o apelido, ou o cargo). Mapeia para o id correto e devolve-o.',
    parameters: {
      type: 'object',
      properties: {
        candidate_id: {
          type: 'string',
          description: 'O id do candidato escolhido. Tem de ser exatamente um dos valores permitidos.',
          enum: candidateIds,
        },
      },
      required: ['candidate_id'],
    },
  };
}
